<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ProcedureEditorSheet from './ProcedureEditorSheet.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['openQuickStart', 'openLegacyProduct']);
const store = useStore();

const isSheetOpen = ref(false);
const editingProcedureId = ref(null);
const editingInitialData = ref({});
const procedureEditorRef = ref(null);
const isSaving = ref(false);
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const aiCloserUIFlags = computed(() => store.getters['aiCloser/getUIFlags'] || {});
const isLoadingConfig = computed(() => aiCloserUIFlags.value.isFetchingConfig);

const runtimeConfig = computed(() => {
  const cfg = aiCloserConfig.value;
  if (!cfg) return null;
  if (cfg.assistant_id && props.assistant?.id) {
    if (Number(cfg.assistant_id) !== Number(props.assistant.id)) return null;
  }
  return cfg;
});

const runtimePromptSections = computed(() => runtimeConfig.value?.prompt_sections || {});
const runtimeSlotSchema = computed(
  () => runtimePromptSections.value?.slot_schema || { enabled: false, intents: {} }
);

const procedures = computed(() => {
  const slotSchema = runtimeSlotSchema.value;
  if (!slotSchema || !slotSchema.intents) return [];

  return Object.entries(slotSchema.intents).map(([intentName, spec]) => {
    const stepsCount = spec.order ? spec.order.length : 0;
    return {
      id: intentName,
      name: intentName,
      steps: stepsCount,
      usedIn: 'AI Agent',
      lastUpdated: '—', // We don't have this info in config
      spec,
    };
  });
});

const openCreateSheet = () => {
  editingProcedureId.value = null;
  editingInitialData.value = {};
  isSheetOpen.value = true;
};

const openEditSheet = (proc) => {
  editingProcedureId.value = proc.id;
  editingInitialData.value = proc.spec;
  isSheetOpen.value = true;
};

const closeSheet = () => {
  isSheetOpen.value = false;
};

const fetchRuntimeConfig = async () => {
  if (!props.assistant?.id) return;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    useAlert(error?.message || 'Failed to load AI runtime config');
  }
};

const saveProcedure = async (payload) => {
  isSaving.value = true;
  try {
    const existingPromptSections = JSON.parse(
      JSON.stringify(runtimePromptSections.value || {})
    );
    const slotSchema = JSON.parse(
      JSON.stringify(existingPromptSections.slot_schema || {})
    );

    if (!slotSchema.enabled) slotSchema.enabled = true;
    if (!slotSchema.intents) slotSchema.intents = {};

    // If renaming, delete old
    if (payload.id && payload.id !== payload.newName) {
      delete slotSchema.intents[payload.id];
    }

    slotSchema.intents[payload.newName] = payload.spec;

    await store.dispatch('aiCloser/updateConfig', {
      assistantId: props.assistant.id,
      data: {
        prompt_sections: {
          ...existingPromptSections,
          slot_schema: slotSchema,
        },
      },
    });

    useAlert('Procedure saved to AI runtime config');
    closeSheet();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to save procedure'
    );
  } finally {
    isSaving.value = false;
  }
};

const canSaveFromShell = () =>
  Boolean(isSheetOpen.value && procedureEditorRef.value?.canSave?.());

const saveFromShell = async () => {
  if (!isSheetOpen.value) {
    useAlert('Open a procedure draft to save it from the top bar');
    return false;
  }

  const payload = procedureEditorRef.value?.buildPayload?.();
  if (!payload) {
    useAlert('Complete the procedure name and at least one step before saving');
    return false;
  }

  await saveProcedure(payload);
  return true;
};

onMounted(() => {
  fetchRuntimeConfig();
});

defineExpose({
  save: saveFromShell,
  canSave: canSaveFromShell,
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div
      class="flex flex-col h-full min-h-0 overflow-hidden rounded-module bg-base-module shadow-lvl-0"
    >
      <!-- Header -->
      <div class="border-b border-neutral-border">
        <div class="mb-1">
          <div class="fin-page-header-row">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn o__secondary o__icon o__subtle"
                aria-label="Toggle sidebar"
              >
                <span class="i-lucide-panel-left-open size-4" />
              </button>
              <h1 class="heading o__size-1">Procedures</h1>
            </div>

            <div class="flex items-center space-x-2">
              <button type="button" class="btn o__secondary o__dropdown-opener">
                <span class="inline-flex items-center gap-2">
                  <span class="i-lucide-book-open-text size-4" />
                  Learn
                </span>
                <span class="i-lucide-chevron-down size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="fin-page-body">
        <div class="fin-page-section">
          <h3 class="text-h3 font-semibold mb-2">Create procedures</h3>
          <p class="text-sm fin-muted">
            Procedures are deterministic workflows (collect fields, validate,
            branch, and exit). This page now reads and writes the runtime
            <code class="px-1 py-0.5 rounded bg-neutral-container">ai_closer_config.prompt_sections.slot_schema</code>
            owner. Open a procedure draft and use the shell Save action to persist
            it through the same top-level flow as the other real owner pages.
          </p>

          <div class="mt-4 border border-neutral-border rounded-large p-4">
            <div class="flex items-center justify-between gap-3">
              <div>
                <div class="font-semibold">No procedures yet</div>
                <div class="text-sm fin-muted mt-1">
                  Create a procedure to guide the AI step-by-step.
                </div>
              </div>
              <button type="button" class="btn o__secondary" @click="openCreateSheet">
                <span class="i-lucide-plus size-4" />
                Add procedure
              </button>
            </div>
          </div>

          <div class="mt-4 text-sm flex flex-wrap gap-3">
            <button type="button" class="fin-link" @click="$emit('openQuickStart')">
              Open Quick Start in Get Started
            </button>
            <button
              type="button"
              class="fin-link"
              @click="$emit('openLegacyProduct', 'answer_templates')"
            >
              Open answer templates in Content
            </button>
          </div>
        </div>

        <div>
          <h3 class="text-h3 font-semibold mb-3">Your procedures</h3>
          <div class="fin-table">
            <table>
              <thead>
                <tr>
                  <th style="width: 44%">Procedure</th>
                  <th style="width: 18%">Steps</th>
                  <th style="width: 18%">Used in</th>
                  <th style="width: 20%">Last updated</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoadingConfig">
                  <td class="fin-muted" colspan="4">Loading runtime procedures...</td>
                </tr>
                <tr v-else-if="!procedures.length">
                  <td class="fin-muted">No procedures yet</td>
                  <td class="fin-muted">—</td>
                  <td class="fin-muted">—</td>
                  <td class="fin-muted">—</td>
                </tr>
                <tr v-for="proc in procedures" :key="proc.id" class="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800" @click="openEditSheet(proc)">
                  <td class="font-medium">{{ proc.name }}</td>
                  <td>{{ proc.steps }}</td>
                  <td>{{ proc.usedIn }}</td>
                  <td class="fin-muted">{{ proc.lastUpdated }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ProcedureEditorSheet
    ref="procedureEditorRef"
    :show="isSheetOpen"
    :procedure-id="editingProcedureId"
    :initial-data="editingInitialData"
    :is-saving="isSaving"
    @close="closeSheet"
    @save="saveProcedure"
  />
</template>

<style scoped>
:global(body.dark) .fin-ai,
:global(body.theme-mix) .fin-ai {
  color: #111827 !important;
}

:global(body.dark) .fin-ai .bg-base-module,
:global(body.theme-mix) .fin-ai .bg-base-module {
  background: #ffffff !important;
}

:global(body.dark) .fin-ai :is(h1, h2, h3, h4, th, .heading, .font-semibold, .fin-link),
:global(body.theme-mix) .fin-ai :is(h1, h2, h3, h4, th, .heading, .font-semibold, .fin-link) {
  color: #111827 !important;
}

:global(body.dark) .fin-ai .fin-muted,
:global(body.theme-mix) .fin-ai .fin-muted {
  color: #6b7280 !important;
}
</style>

